import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { formatCurrency } from 'helpers';
import { EstateBalanceSheet_FieldsFragment } from 'queries';

const useStyles = makeStyles(() => ({
  tableCell: {
    border: '1px solid #E0E0E0',
  },
  tableCellLabel: {
    width: '65%',
  },
  tableContainer: {
    marginBottom: '24px',
    marginTop: '16px',
  },
  title: {
    fontWeight: 'bold',
    fontSize: '16px',
  },
}));

type Props = {
  estateBalanceSheet: EstateBalanceSheet_FieldsFragment;
};

type Row = [string, number];

const TableComponent = ({ rows }: { rows: Row[] }) => {
  const classes = useStyles();

  return (
    <div className={classes.tableContainer}>
      <TableContainer component={Paper}>
        <Table aria-label="Balance Sheet Calculations">
          <TableBody>
            {rows.map(([label, value]) => (
              <TableRow key={label}>
                <TableCell
                  component="th"
                  scope="row"
                  className={clsx(classes.tableCell, classes.tableCellLabel)}
                >
                  {label}
                </TableCell>
                <TableCell align="right" className={classes.tableCell}>
                  {formatCurrency(String(value * 100))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

const EstateBalanceSheetCalculationsTable = ({ estateBalanceSheet }: Props) => {
  const classes = useStyles();

  const rows: Row[] = [
    ['Total Assets', estateBalanceSheet.totalAssets],
    ['Total Liabilities', estateBalanceSheet.totalLiabilities],
    ['Total Deductible Liabilities', estateBalanceSheet.totalDeductibleLiabilities],
    ['Probate Assets', estateBalanceSheet.probateAssets],
    ['Non-Probate Assets (Taxable)', estateBalanceSheet.nonProbateAssetsTaxable],
    ['Non-Probate Assets (Non-Taxable)', estateBalanceSheet.nonProbateAssetsNonTaxable],
    ['Gross Estate For Estate Tax Purposes', estateBalanceSheet.grossEstateForEstateTaxPurposes],
  ];

  const federalExemptionRows: Row[] = [
    [
      'Federal Exemption for Year of Death',
      estateBalanceSheet.federalExemptionYearOfDeathAmount || 0,
    ],
    ['Exemption used per last Form 709', estateBalanceSheet.exemptionUsedLastForm709 || 0],
    ['Exemption Available', estateBalanceSheet.exemptionAvailable],
  ];

  const deductionsRows: Row[] = [
    [
      'Non-Exempt Taxable Estate',
      estateBalanceSheet.nonExemptTaxableEstate > 0 ? estateBalanceSheet.nonExemptTaxableEstate : 0,
    ],
    ['Deductions', estateBalanceSheet.deductions],
  ];

  const estimatedEstateTaxRows: Row[] = [
    [
      `${estateBalanceSheet.estimatedEstateTaxPercentage}%`,
      estateBalanceSheet.estimatedEstateTax > 0 ? estateBalanceSheet.estimatedEstateTax : 0,
    ],
  ];

  return (
    <>
      <TableComponent rows={rows} />
      <Typography className={classes.title}>Federal Exemption</Typography>
      <TableComponent rows={federalExemptionRows} />
      <TableComponent rows={deductionsRows} />
      <Typography className={classes.title}>Estimated Estate Tax</Typography>
      <TableComponent rows={estimatedEstateTaxRows} />
    </>
  );
};

export default EstateBalanceSheetCalculationsTable;
